import React, { useState } from "react"
import { Sidebar } from "../components/ui/sidebar"
import { CareerHeader } from "../components/ui/career-learning-path/career-learning-header"
import { CareerTabs } from "../components/ui/career-learning-path/career-tabs"
import { LearningJourney } from "../components/ui/career-learning-path/learning-journey"
import { SkillTracker } from "../components/ui/career-learning-path/skill-tracker"
import { CourseProgressJourney } from "../components/ui/career-learning-path/course-progress-journey"
import { CurriculumTracker } from "../components/ui/career-learning-path/curriculum-tracker"
import { AssignmentsProjects } from "../components/ui/career-learning-path/assignments-projects"
import { CareerSidebarCard } from "../components/ui/career-learning-path/career-sidebar-card"
import { UniversalSearch } from "../components/ui/universal-search"
import {AIChatBox} from "../components/ui/ai-chat-box";
import ProtectedRoute from "../components/ProtectedRoute"


const CHEF_CAREER_DATA = {
  name: "Chef",
  description:
    "A chef is a professional cook and tradesman who is proficient in all aspects of food preparation, often focusing on a particular cuisine.",
  matchPercentage: 85,
  category: "Commercial-Related",
  image:
    "https://lh3.googleusercontent.com/aida-public/AB6AXuDBWKCJsCt9b2h2eizssnIIxW1AL_jeZ5y5agOlRwcaGjgqZDHZniygBplXywUyuTdPT7xY8y-dlZvIXg3FXmbGXypUsHVdi_bo8wIKU6oRm6sJeUplgvhr1ZSNZLknBgGVYTovDQ8uOFhoiSfRF1sCc9bfSp5koihMCiRCsnn9qUHTFK3BNjky0PqpceUEa6Kqd_bA8ycyIZQVt00t3elDR-Q3JmlZJfnbdxV6NCRxHlO164I57MeAVPIZeaG3knMF7jotygXndN8",
}

const LEARNING_JOURNEY = [
  { level: "Jss1", subjects: [
      { name: "Home Economics", type: "Compulsory" },
      { name: "Basic Science", type: "Compulsory" },
      { name: "Fine Art", type: "Optional" },
    ] 
  },
  { level: "Jss2", subjects: [
      { name: "Home Economics", type: "Compulsory" },
      { name: "Business Studies", type: "Compulsory" },
    ] 
  },
  { level: "Jss3", subjects: [
      { name: "Agricultural Science", type: "Compulsory" }
    ] 
  },
  { level: "Ss1", subjects: [
      { name: "Chemistry", type: "Compulsory" },
      { name: "Biology", type: "Optional" },
    ] 
  },
  { level: "Ss2", subjects: [
      { name: "Food and Nutrition", type: "Compulsory" }
    ] 
  },
  { level: "Ss3", subjects: [
      { name: "Economics", type: "Optional" }
    ] 
  },
]

const SKILLS = [
  { name: "Creativity", percentage: 80 },
  { name: "Time Management", percentage: 65 },
  { name: "Teamwork", percentage: 92 },
  { name: "Attention to Detail", percentage: 75 },
]

const PROGRESS_MILESTONES = [
  { id: 1, label: "First Recipe", completed: true },
  { id: 2, label: "Kitchen Safety", completed: true },
  { id: 3, label: "Baking Basics", completed: false, current: true },
  { id: 4, label: "Grilling 101", completed: false },
  { id: 5, label: "World Cuisine", completed: false },
]

const CURRICULUM_ITEMS = [
  { id: 1, name: "Basic Science (JSS1)", subject: "Science", progress: 100, status: "completed" },
  { id: 2, name: "Home Economics (JSS2)", subject: "Home Economics", progress: 60, status: "in-progress" },
  { id: 3, name: "Chemistry (SS1)", subject: "Chemistry", progress: 0, status: "upcoming" },
  { id: 4, name: "Food and Nutrition (SS2)", subject: "Food & Nutrition", progress: 0, status: "locked" },
]

const ASSIGNMENTS = [
  { id: 1, name: "Bake a Simple Cake", subject: "Home Economics", dueDate: "Oct 25, 2023", status: "in-progress" },
  { id: 2, name: "Photosynthesis Report", subject: "Basic Science", dueDate: "Oct 28, 2023", status: "pending" },
  { id: 3, name: "Safety in the Kitchen Poster", subject: "Home Economics", dueDate: "Oct 15, 2023", status: "submitted" },
  { id: 4, name: "Business Plan Project", subject: "Business Studies", dueDate: "Oct 12, 2023", status: "overdue" },
]

// ----------------------------
// REACT PAGE COMPONENT
// ----------------------------
export default function CareerLearningPathPage() {
  const [activeTab, setActiveTab] = useState("Learning Path")
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const handleBack = () => window.history.back()
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)
  const closeSidebar = () => setIsSidebarOpen(false)

  const handleSearch = (query) => {
    console.log("Universal search for:", query)
  }

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-200">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden bg-black/50 backdrop-blur-sm"
          onClick={closeSidebar}
        />
      )}

      {/* Mobile Hamburger */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md md:hidden"
      >
        <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`fixed md:sticky top-0 left-0 z-40 h-screen w-64 bg-white border-r border-slate-200
        transform transition-transform duration-300
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="h-full overflow-y-auto">
          <Sidebar />
        </div>
      </div>

      {/* Universal Search */}
      <UniversalSearch onSearch={handleSearch} />

      <AIChatBox />

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
        
        {/* Mobile spacing */}
        <div className="md:hidden h-16"></div>

        {/* Career Header */}
        <div className="max-w-7xl mx-auto space-y-8">
          <CareerHeader
            careerName={CHEF_CAREER_DATA.name}
            description={CHEF_CAREER_DATA.description}
            matchPercentage={CHEF_CAREER_DATA.matchPercentage}
            category={CHEF_CAREER_DATA.category}
            imageUrl={CHEF_CAREER_DATA.image}
            onBack={handleBack}
          />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* LEFT CONTENT */}
            <div className="lg:col-span-3 space-y-8">

              <CareerTabs activeTab={activeTab} onTabChange={setActiveTab} />

              {/* TAB 1 - Learning Path */}
              {activeTab === "Learning Path" && (
                <div className="space-y-8">
                  <LearningJourney gradeLevels={LEARNING_JOURNEY} />
                  <SkillTracker skills={SKILLS} />
                </div>
              )}

              {/* TAB 2 - My Learning Journey */}
              {activeTab === "My Learning Journey" && (
                <div className="bg-slate-100 p-6 rounded-2xl shadow space-y-8">
                  <CourseProgressJourney milestones={PROGRESS_MILESTONES} />
                  <CurriculumTracker items={CURRICULUM_ITEMS} />
                  {/* <AssignmentsProjects assignments={ASSIGNMENTS} onViewAssignment={() => {}} /> */}
                </div>
              )}
            </div>

            {/* RIGHT SIDEBAR CARD */}
            <div className="lg:col-span-1">
              <div className="bg-slate-100 p-6 rounded-2xl shadow border border-gray-100 sticky top-16">
                <CareerSidebarCard />
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
    </ProtectedRoute>
  )
}
